import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { log } from '@/services/Log'
import type { BCCheckoutType } from '@/types/BigCommerce/BCCheckout'
import type { StandardOrder } from '@/types/ShopFront/CheckoutStandards'

export const setCartItemQuantity = async ({
  shipmentId, id, quantity, order,
}: {
  order :StandardOrder,
  quantity: number,
  shipmentId: string,
  id: string
}) => {
  log('setCartItemQuantity', {
    shipmentId, id, quantity, order,
  })
  try {
    const [
      { default: Axios },
      { cartUpdateQuantity },
      { getCartFromResponse },
      { default: recoverShippingOptions },
      { standardizeAddressForBigC },
      { standardizeOrder },
      { removeCartItem },
      { goToCart },
      { areAllShipmentsAssigned },
    ] = await allPromisesWithRetries(() => [
      import('axios'),
      import('@/services/Cart/cartUpdateQuantity'),
      import('./getCartFromResponse'),
      import('../shipmentServices/recoverShippingOptions'),
      import('@/services/Standardizers/standardizeAddressForBigC'),
      import('@/services/Standardizers/standardizeOrder'),
      import('./removeCartItem'),
      import('@/services/FramedCheckout/navigationServices'),
      import('@/helpers/areAllShipmentsAssigned'),
    ])

    if (quantity === 0) {
      return await removeCartItem({
        id, order,
      })
    }
    let newOrder: StandardOrder = { ...order }
    const shipment = order.shipments.find((shipment) => shipmentId === shipment.id)
    // Updates a Cart line item - https://developer.bigcommerce.com/api-reference/storefront/carts/cart-items/updatecartlineitem
    // Updates an existing, single line item quantity and the price of custom items in a cart.

    // const warrantyId = getWarrantyId({ lineItems: order?.cart.lineItems, id })
    const response = await cartUpdateQuantity({ itemId: id, cartId: order?.cart.id, quantity })
    const { newCart } = response
    if (!newCart) {
      await goToCart()
    } else if (response.success) { // Shipping option can be lost while interacting with the cart
      if (shipment?.selectedShippingOption) {
        const data = {
          shippingAddress: standardizeAddressForBigC(shipment?.shippingAddress),
          lineItems: shipment?.lineItems.map(({ id: itemId, quantity: itemQuantity }) => ({
            itemId,
            quantity: (
              newCart.lineItems.find((item) => item.id === itemId)?.quantity || itemQuantity
            ),
          })),
        }
        const url = `/api/storefront/checkouts/${order?.cart?.id}/consignments/${shipment?.id}?include=consignments.availableShippingOptions`
        try {
          const consignmentUpdateResponse = await Axios.put<BCCheckoutType>(
            url,
            data,
          )
          const rawCart = consignmentUpdateResponse?.data?.cart
          const cart = getCartFromResponse(rawCart, order)
          newOrder = await standardizeOrder(consignmentUpdateResponse?.data, cart)
          log('setCartItemQuantity => recoverShippingOptions', { order, newOrder, areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder) })
          return await recoverShippingOptions({ order, newOrder, newCart: newOrder.cart })
        } catch {
          log('setCartItemQuantity Consignment quantity update FAILED', {
            success: false,
            url,
            data,
          })
          return { success: false }
        }
      }
      log('setCartItemQuantity => recoverShippingOptions', {
        order, newCart,
      })
      return await recoverShippingOptions({ order, newCart })
    }
    return { success: false }
  } catch {
    return { success: false }
  }
}

export default setCartItemQuantity
